import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { dashboardSummary, setFounderType as apiSetFT } from "../lib/api";
import { useAuth } from "../lib/auth";
import AppShell from "./AppShell";

function verdictBadge(v) {
  if (!v) return <span className="badge badge-neutral">NO VERDICT</span>;
  if (v === "pursue") return <span className="badge badge-pursue">PURSUE</span>;
  if (v === "pivot")  return <span className="badge badge-pivot">PIVOT</span>;
  return <span className="badge badge-kill">KILL</span>;
}

function FounderTypeModal({ onChosen }) {
  const types = [
    { k: "first_time", t: "First-time founder", d: "Starting your first thing. I'll push you harder on core assumptions." },
    { k: "technical", t: "Technical builder", d: "You can ship. I'll push you on distribution, not features." },
    { k: "non_technical", t: "Non-technical", d: "You move through conversations. I'll push scrappy validation." },
    { k: "repeat", t: "Repeat founder", d: "You've done this before. I'll skip basics and go strategic." },
  ];
  const [choice, setChoice] = useState(null);
  const [saving, setSaving] = useState(false);

  return (
    <div className="fixed inset-0 bg-zinc-950/40 backdrop-blur-sm z-30 flex items-center justify-center p-4" data-testid="founder-type-modal">
      <div className="card max-w-2xl w-full p-8 fade-up">
        <div className="overline mb-3">first, one question</div>
        <h3 className="font-display text-3xl font-semibold tracking-tight mb-2">What kind of founder are you?</h3>
        <p className="text-zinc-600 mb-8">I&apos;ll adapt how blunt I am and what I push you on.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {types.map((t) => (
            <button
              key={t.k}
              onClick={() => setChoice(t.k)}
              data-testid={`founder-type-${t.k}`}
              className={`text-left p-4 border rounded-md transition-all ${choice === t.k ? "border-zinc-950 bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"}`}
            >
              <div className="font-display font-medium text-base mb-1">{t.t}</div>
              <div className="text-xs text-zinc-600 leading-relaxed">{t.d}</div>
            </button>
          ))}
        </div>
        <div className="mt-8 flex justify-end">
          <button
            disabled={!choice || saving}
            data-testid="founder-type-submit"
            className="btn btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={async () => {
              if (!choice) return;
              setSaving(true);
              try {
                await apiSetFT(choice);
                onChosen(choice);
              } finally {
                setSaving(false);
              }
            }}
          >
            Continue →
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const s = await dashboardSummary();
      setSummary(s);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const needsFounderType = user && !user.founder_type;

  return (
    <AppShell>
      <div className="flex-1 flex flex-col">
      {needsFounderType && (
        <FounderTypeModal
          onChosen={async () => {
            await refresh();
          }}
        />
      )}

      <div className="flex items-end justify-between mb-10 fade-up">
        <div>
          <div className="overline mb-2">dashboard</div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter">
            {user?.name?.split(" ")[0] || "Founder"}.{" "}
            <span className="font-serif italic text-zinc-500">Here&apos;s the call.</span>
          </h1>
        </div>
        <button
          onClick={() => navigate("/new")}
          className="btn btn-primary"
          data-testid="new-idea-btn"
        >
          + New idea
        </button>
      </div>

      {loading ? (
        <div className="overline" data-testid="dashboard-loading">loading…</div>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10 stagger">
            <StatCard label="Active ideas" value={summary?.total ?? 0} testId="stat-total" />
            <StatCard label="Pursue" value={summary?.pursue ?? 0} tone="pursue" testId="stat-pursue" />
            <StatCard label="Pivot" value={summary?.pivot ?? 0} tone="pivot" testId="stat-pivot" />
            <StatCard label="Kill" value={summary?.kill ?? 0} tone="kill" testId="stat-kill" />
            <StatCard label="Best streak" value={summary?.top_streak ?? 0} testId="stat-streak" />
          </div>

          {/* Ideas list */}
          {summary?.ideas?.length ? (
            <div className="border border-zinc-200 rounded-md divide-y divide-zinc-200 bg-white" data-testid="ideas-list">
              <div className="px-5 py-3 flex items-center justify-between text-xs font-mono uppercase tracking-[0.15em] text-zinc-500">
                <span>Your ideas</span>
                <span>{summary.ideas.length} total</span>
              </div>
              {summary.ideas.map((i) => (
                <Link
                  to={`/ideas/${i.idea_id}`}
                  key={i.idea_id}
                  data-testid={`idea-row-${i.idea_id}`}
                  className="px-5 py-5 flex items-center gap-5 hover:bg-zinc-50 transition-colors group"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      {verdictBadge(i.latest_verdict)}
                      {i.streak > 0 && (
                        <span className="text-xs font-mono text-zinc-500">streak · {String(i.streak).padStart(2, "0")}</span>
                      )}
                    </div>
                    <div className="font-display text-lg font-medium tracking-tight truncate">{i.title}</div>
                    <div className="text-sm text-zinc-600 truncate">{i.one_liner}</div>
                  </div>
                  <div className="hidden md:grid grid-cols-3 gap-6 text-right w-72">
                    <Score label="FMF" val={i.latest_fmf_score} />
                    <Score label="WTP" val={i.latest_wtp_score} />
                    <Score label="Momentum" val={i.latest_momentum_score} />
                  </div>
                  <div className="text-zinc-400 group-hover:text-zinc-950 transition-colors">→</div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="flex-1 flex items-center">
              <div className="card p-12 text-center w-full" data-testid="empty-state">
              <div className="overline mb-3">nothing to diagnose yet</div>
              <h3 className="font-display text-3xl font-semibold tracking-tight mb-3">Got an idea to pressure-test?</h3>
              <p className="text-zinc-600 mb-6 max-w-md mx-auto">Brief it in 2 minutes. I&apos;ll tell you if it&apos;s worth pursuing, what to pivot to, or when to kill it.</p>
              <button onClick={() => navigate("/new")} className="btn btn-primary" data-testid="empty-new-idea-btn">Brief your first idea →</button>
              </div>
            </div>
          )}
        </>
      )}
      </div>
    </AppShell>
  );
}

function StatCard({ label, value, tone, testId }) {
  const cls = tone === "pursue" ? "text-emerald-600" : tone === "pivot" ? "text-amber-600" : tone === "kill" ? "text-red-600" : "text-zinc-950";
  return (
    <div className="card p-5" data-testid={testId}>
      <div className="overline mb-2">{label}</div>
      <div className={`font-display text-3xl font-semibold ${cls}`}>{value}</div>
    </div>
  );
}

function Score({ label, val }) {
  if (val === null || val === undefined) return <div className="text-xs text-zinc-400 font-mono">-</div>;
  const tone = val >= 70 ? "text-emerald-600" : val >= 40 ? "text-amber-600" : "text-red-600";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-mono">{label}</div>
      <div className={`font-mono text-sm ${tone}`}>{val}</div>
    </div>
  );
}
